import type Mmenu from 'mmenu-js';
import type { MmenuNavbarConfig } from 'mmenu-js';

const MOBILE_MAX_WIDTH = 800;

const SELECTOR_MENU = '.js-mobile-menu';
const SELECTOR_TRIGGER = '.js-mobile-menu-trigger';
const SELECTOR_CLOSE = '.js-mobile-menu-close';
const SELECTOR_NAVBAR_TOP = '.js-mobile-menu-top';
const SELECTOR_NAVBAR_BOTTOM = '.js-mobile-menu-bottom';
const SELECTOR_PAGE = '#page';

const TRIGGER_ARIA_LABEL_OPEN = 'Open menu';
const TRIGGER_ARIA_LABEL_CLOSE = 'Close menu';

const boundTriggers = new WeakSet<HTMLElement>();

function isAccountSpa(): boolean {
  return document.getElementById('account-app') !== null;
}

function isMobileViewport(): boolean {
  return window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`).matches;
}

function cloneTemplateChildren(selector: string): HTMLElement[] {
  const template = document.querySelector<HTMLTemplateElement>(selector);
  if (!template) {
    return [];
  }

  return Array.from(template.content.children).map((node) => {
    return node.cloneNode(true) as HTMLElement;
  });
}

function getNavbars(): MmenuNavbarConfig[] {
  const navbars: MmenuNavbarConfig[] = [];

  const topContent = cloneTemplateChildren(SELECTOR_NAVBAR_TOP);
  if (topContent.length > 0) {
    navbars.push({ position: 'top', content: topContent });
  }

  const bottomContent = cloneTemplateChildren(SELECTOR_NAVBAR_BOTTOM);
  if (bottomContent.length > 0) {
    navbars.push({ position: 'bottom', content: bottomContent });
  }

  return navbars;
}

function getOffCanvasPosition(): string {
  // Overlay mode keeps the Vue SPA layout intact after the menu closes.
  return isAccountSpa() ? 'left-front' : 'left';
}

function resetAccountPageLayout(): void {
  if (!isAccountSpa()) {
    return;
  }

  const wrapper = document.querySelector<HTMLElement>('.mm-wrapper');
  const slideout = wrapper?.querySelector<HTMLElement>('.mm-slideout');

  slideout?.style.removeProperty('transform');
  slideout?.style.removeProperty('width');
  document.body.style.removeProperty('overflow');
}

function bindMenuAccessibility(
  menuInstance: Mmenu,
  menuElement: HTMLElement,
  triggerElement: HTMLElement,
): void {
  const setOpen = (): void => {
    triggerElement.setAttribute('aria-expanded', 'true');
    triggerElement.setAttribute('aria-label', TRIGGER_ARIA_LABEL_CLOSE);
    menuElement.removeAttribute('aria-hidden');
    menuElement.removeAttribute('hidden');
  };

  const setClosed = (): void => {
    triggerElement.setAttribute('aria-expanded', 'false');
    triggerElement.setAttribute('aria-label', TRIGGER_ARIA_LABEL_OPEN);
    // `hidden` already takes the menu out of the accessibility tree, so the two
    // attributes are only ever set one at a time.
    if (isAccountSpa()) {
      menuElement.setAttribute('aria-hidden', 'true');
    }
    else {
      menuElement.removeAttribute('aria-hidden');
      menuElement.setAttribute('hidden', '');
    }

    resetAccountPageLayout();
  };

  menuInstance.bind('open:before', () => {
    menuElement.removeAttribute('hidden');
  });
  menuInstance.bind('open:after', setOpen);
  menuInstance.bind('close:after', setClosed);
}

function bindMenuClose(menuInstance: Mmenu, root: HTMLElement): void {
  root.querySelectorAll<HTMLButtonElement>(SELECTOR_CLOSE).forEach((button) => {
    button.addEventListener('click', (event) => {
      event.preventDefault();
      menuInstance.close();
    });
  });
}

function expandSubmenus(menuInstance: Mmenu, root: HTMLElement): void {
  const api = menuInstance as unknown as {
    openPanel: (panel: HTMLElement, animation?: boolean, setfocus?: boolean) => void
  };

  root
    .querySelectorAll<HTMLElement>('.mm-listitem--vertical > .mm-panel')
    .forEach((subpanel) => {
      api.openPanel(subpanel, false, false);
    });
}

function lockSubmenus(root: HTMLElement): void {
  root
    .querySelectorAll<HTMLElement>('.mm-listitem--vertical > .mm-listitem__btn')
    .forEach((button) => {
      button.removeAttribute('href');
      button.removeAttribute('aria-label');
    });
}

function openMenuWithAnimation(menuInstance: Mmenu): void {
  // Let mmenu paint the closed state before opening (needed after lazy init).
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      menuInstance.open();
    });
  });
}

export function initMobileMenu(): void {
  if (!isMobileViewport()) {
    return;
  }

  const menuElement = document.querySelector<HTMLElement>(SELECTOR_MENU);
  const triggerElement = document.querySelector<HTMLElement>(SELECTOR_TRIGGER);

  if (!menuElement || !triggerElement || boundTriggers.has(triggerElement)) {
    return;
  }

  boundTriggers.add(triggerElement);

  let menuInstance: Mmenu | null = null;
  let isLoading = false;

  const loadMenu = async (): Promise<void> => {
    try {
      const [{ default: MmenuConstructor }] = await Promise.all([
        import('mmenu-js'),
        import('mmenu-js/dist/mmenu.css'),
      ]);

      const navbars = getNavbars();

      menuInstance = new MmenuConstructor(
        menuElement,
        {
          ...(navbars.length > 0 && { navbars }),
          navbar: {
            add: false,
          },
          slidingSubmenus: false,
          offCanvas: {
            position: getOffCanvasPosition(),
          },
        },
        {
          offCanvas: {
            page: { selector: SELECTOR_PAGE },
          },
        },
      );

      bindMenuAccessibility(menuInstance, menuElement, triggerElement);
      bindMenuClose(menuInstance, menuElement);
      expandSubmenus(menuInstance, menuElement);
      lockSubmenus(menuElement);
      openMenuWithAnimation(menuInstance);
    }
    catch (error) {
      console.warn('Failed to load mobile menu', error);
    }
  };

  const openMenu = async (): Promise<void> => {
    if (menuInstance) {
      menuInstance.open();
      return;
    }

    if (isLoading) {
      return;
    }

    isLoading = true;
    await loadMenu().finally(() => {
      isLoading = false;
    });
  };

  triggerElement.addEventListener('click', () => {
    void openMenu();
  });
}
